import { Button, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import React, { Suspense } from 'react';
import { AnyPassport, DevicePassportResources } from '../../models/warehouse-models';
import { useAsyncResource } from 'use-async-resource';
import { useGetPassportImages, useGetPassportImageUrls } from '../../utils/warehouses-hooks';
import { checkered } from '../../styles/selectStyles';
import noImage from '../../assets/No_image_available.png';
interface PassportDetailsProps {
    item?: AnyPassport;
    setSelectedItem: (item?: AnyPassport) => void;
    title: string;
}

export const PassportImages = ({ id }: { id: number }) => {
    const [getImageUrls] = useAsyncResource(useGetPassportImageUrls, id);
    const [getImages] = useAsyncResource(useGetPassportImages, getImageUrls());
    const images = id !== 0 ? getImages() : [];
    const [imageUrl] = images.length > 0 ? images.map(URL.createObjectURL) : [noImage];
    return (
        <>
            {imageUrl === noImage ? (
                <img className='passportImage noImg' src={noImage} alt={`Device img`} />
            ) : (
                <img className='passportImage' src={imageUrl} alt={`Device img`} />
            )}
        </>
    );
};

export const PassportDetails = ({ item, setSelectedItem, title }: PassportDetailsProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });

    return (
        <Modal show={!!item} onHide={() => setSelectedItem(undefined)} size='lg' centered>
            <Modal.Header closeButton>
                <Modal.Title id='contained-modal-title-vcenter'>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{!!item ? <PassportDetailsInner item={item} /> : <></>}</Modal.Body>
            <Modal.Footer>
                <Button onClick={() => setSelectedItem(undefined)}>{t('close')}</Button>
            </Modal.Footer>
        </Modal>
    );
};

const PassportDetailsInner = ({ item }: { item: AnyPassport }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'table' });
    const { t: commonTranslation } = useTranslation('translation', { keyPrefix: 'common' });
    const fields = {
        passport: [
            { name: 'name' },
            {
                name: 'type',
                format: (s: string | string[]) => Array.isArray(s) ? "" : commonTranslation(s.toLowerCase())
            },
            {
                name: 'price',
                unit: '€',
                format: (s: string | string[]) => Array.isArray(s) ? "" : Number(s).toFixed(2)
            },
            {
                name: 'priceForInvoice',
                unit: '€',
                format: (s: string | string[]) => Array.isArray(s) ? "" : Number(s).toFixed(2)
            },
            {
                name: 'priceForSale',
                unit: '€',
                format: (s: string | string[]) => Array.isArray(s) ? "" : Number(s).toFixed(2)
            },
        ],
        device: [
            { name: 'model' },
            {
                name: 'minimumDeposit',
                unit: '€',
                format: (s: string | string[]) => Array.isArray(s) ? '' : Number(s).toFixed(2)
            },
            { name: 'power', unit: 'W' },
            { name: 'areaCoverage' },
            { name: 'sprayRange', unit: 's' },
            { name: 'waitRange', unit: 's' },
        ],
        deviceResources: [
            { name: 'oilConsumption', unit: 'ml' },
            { name: 'oilCapacity', unit: 'ml' },
        ],
        batteryResources: [
            { name: 'batteryModel' },
            { name: 'batteryConsumptionOn', unit: 'mAh' },
            { name: 'batteryConsumptionStandBy', unit: 'mAh' },
            { name: 'batteryCount' },
        ],
        oil: [
            {
                name: 'forDeviceModel',
                format: (s: string[] | string) =>
                    Array.isArray(s) ?
                        (s.length === 0 ? t('all') : s.join(', ')) : (s === null || s === undefined ? t('all') : s)
            },
            { name: 'oilCapacity', unit: 'ml' }
        ],
        battery: [{ name: 'batteryCapacity', unit: 'mAh' }, { name: 'batteryVoltage', unit: 'V' }],
        consumable: [{ name: 'forModel' }],
    };

    const detailsField = (
        fieldName: keyof AnyPassport,
        index: number,
        unit?: string,
        format?: (s: string[] | string) => string
    ) => {
        const value = item[fieldName];
        let formattedValue = format && value !== undefined && value !== null
            ? format(value as string[] | string)
            : value;
        return (
            <div key={index} className={`border d-flex p-2 justify-content-between ${checkered(index)}`}>
                <div>{t(fieldName)}:</div>
                <div className=' ms-4'>
                    {formattedValue} {unit}
                </div>
            </div>
        );
    };
    const resourceField = (
        res: DevicePassportResources,
        fieldName: keyof DevicePassportResources,
        index: number,
        unit?: string
    ) => {
        return (
            <div key={index} className={`border d-flex p-2 justify-content-between ${checkered(index)}`}>
                <div>{t(fieldName)}:</div>
                <div className='ms-4 '>
                    {res[fieldName]} {unit}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className='passportGrid'>
                <div className='w-100 max-w-300 image m-auto'>
                    <Suspense fallback={<Spinner animation={'border'}></Spinner>}>
                        <PassportImages id={item.id} />
                    </Suspense>
                </div>
                <div>
                    <h4 className='mb-3'>{commonTranslation('passport')}</h4>
                    {fields.passport.map(({ name, format }, index) =>
                        detailsField(name as keyof AnyPassport, index, undefined, format)
                    )}
                </div>

                {item.type === 'DEVICE' && (
                    <>
                        <div>
                            <h4 className='mb-3'>{commonTranslation('device')}</h4>
                            <>
                                {fields.device.map(({ name, unit, format }, index) =>
                                    detailsField(name as keyof AnyPassport, index, unit, format)
                                )}
                            </>
                        </div>

                        <div className='full-row'>
                            <h4 className='mb-3'>{commonTranslation('resources')}</h4>
                            {fields.deviceResources.map(({ name, unit }, index) => {
                                return (
                                    item.resources &&
                                    resourceField(item.resources, name as keyof DevicePassportResources, index, unit)
                                );
                            })}
                            {!!item.resources?.batteryCount &&
                                item.resources?.batteryCount > 0 &&
                                fields.batteryResources.map(({ name, unit }, index) => {
                                    return (
                                        item.resources &&
                                        resourceField(
                                            item.resources,
                                            name as keyof DevicePassportResources,
                                            index,
                                            unit
                                        )
                                    );
                                })}
                        </div>
                    </>
                )}
                {item.type === 'OIL' && (
                    <div>
                        <h4 className='mb-3'>{commonTranslation('oil')}</h4>
                        {item.isUniversal ? (
                            <>
                                {detailsField('oilCapacity' as keyof AnyPassport, 0, 'ml')}
                                {item.childPassports && item.childPassports.length > 0 && (
                                    <div className='mt-2'>
                                        <div className='fw-bold mb-1'>{t('childPassports')}:</div>
                                        {item.childPassports.map((cp, index) => (
                                            <div key={cp.id} className={`border d-flex p-2 justify-content-between ${checkered(index)}`}>
                                                <div>{cp.name}</div>
                                                <div className='ms-4'>{cp.forDeviceModel}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {fields.oil.map(({ name, unit, format }, index) =>
                                    detailsField(name as keyof AnyPassport, index, unit, format)
                                )}
                            </>
                        )}
                    </div>
                )}
                {item.type === 'BATTERY' && (
                    <div>
                        <h4 className='mb-3'>{commonTranslation('battery')}</h4>
                        <>
                            {fields.battery.map(({ name, unit }, index) =>
                                detailsField(name as keyof AnyPassport, index, unit)
                            )}
                        </>
                    </div>
                )}
                {item.type === 'OTHER' && (
                    <div>
                        <h5 className='my-2'>{commonTranslation('description')}:</h5>
                        <div>{item.description}</div>
                    </div>
                )}
            </div>
        </>
    );
};
